import React from 'react';
import { includes, filter, isEmpty, get, map, every, cloneDeep, set } from 'lodash';

// React view component to render
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { callStatisticsReports as CallStatisticsReportsView } from 'ui/views/widgets/callStatisticsReports';
import { CallStatisticsReportsService } from 'services/callStatisticsReports';
import { SELECT_ALL_OPTION_VALUE } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { reportCallCallerTypeselectionResolver } from 'resolvers/reportCallCallerTypeselection';
import * as errorOverlay from 'utils/errorOverlay';

// get store
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallStatisticsReports } from 'ui/containers/CallStatisticsReports';
 *
 * // render
 * <CallStatisticsReports propName='propValue'/>
 */
export class CallStatisticsReports extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            isFetching: true,
            isBarChartFetching: true,
            isPieChartFetching: false,
            networkStatus: undefined,
            componentData: undefined,
            barGraphData: {},
            pieChartData: {},
            isPieChartVisible: false,
            barGraphNetworkStatus: undefined
        };

        // create service instance
        this.callStatisticsReportsService = new CallStatisticsReportsService();
        
        // start component data fetch
        this.fetchComponentData();

        // bind events and methods
        this.__bind();
    }

     // bind methods and add event listeners
     __bind() {
        this.handleOptionSelect = this.handleOptionSelect.bind( this );
        this.handleBarChartClick = this.handleBarChartClick.bind( this );
        this.handleCagChange = this.handleCagChange.bind( this );
        this.fetchDataForGraph = this.fetchDataForGraph.bind( this );
        this.fetchDataForChart = this.fetchDataForChart.bind( this );
        this.getSelection = this.getSelection.bind( this );
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'CallStatisticsReports.fetchComponentData()' );

        // cancel ongoing request
        this.callStatisticsReportsService.cancel();

        // get status and service data
        const { status, data } = await this.callStatisticsReportsService.fetchComponentData( {
            url: this.props.widget.url
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                isBarChartFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            const responseData = cloneDeep( data );
            const selectAll = [ { name: SELECT_ALL_OPTION_VALUE, title: { refId: '', value: 'All' }, value: SELECT_ALL_OPTION_VALUE } ];

            const callerMood = [ ...cloneDeep( selectAll ), ...get( responseData, 'options.callerMood' ) ];
            set( responseData, 'filterData.options.callerMood', callerMood );

            // fetching resolution type options
            const resolutionTypeOptions = [ ...cloneDeep( selectAll ), ...get( responseData, 'options.callResolution' ) ]; // data.options.callResolution;
            set( responseData, 'filterData.options.resolutionTypeOptions', resolutionTypeOptions );

            // fetch Caller Types from the store
            const _callerTypeOptions = cloneDeep( get( store.getState()[ REDUCER.APP_CONTEXT ], 'component.data.options.callerType', null ) );
            const callerTypeOptions = [ ...selectAll, ..._callerTypeOptions ];
            set( responseData, 'filterData.options.callerTypeOptions', callerTypeOptions );

            const callCallerType = get( data, 'options.callCallerType' );
            const callCallerTypeOptions = [ ...cloneDeep( selectAll ), ...callCallerType ];

            set( responseData, 'filterData.options.callCallerType', callCallerTypeOptions );
            set( responseData, 'filterData.options.frequencyOptions', get( data, 'options.frequencyOptions' ) );
            
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: responseData,
            }, () => {

                this.fetchDataForGraph();

            } );
        }
    }

    // fetch Data for the bar graph
    async fetchDataForGraph() {
        log.debug( 'CallStatisticsReports.fetchDataForGraph()' );

        // cancel ongoing request
        this.callStatisticsReportsService.cancel();

        // remove all other Options if All is selected and add 'Add'
        const filters = cloneDeep( get( this.state, 'componentData.filterData.filter' ) );
        Object.keys( filters ).forEach( function( key ) {
            includes( filters[ key ], SELECT_ALL_OPTION_VALUE ) ? filters[ key ] = [ SELECT_ALL_OPTION_VALUE ] : null;
        } );

        // get status and service data
        const { status, data } = await this.callStatisticsReportsService.fetchGraphData( {
            url: this.state.componentData.structure.filtersConfig.url,
            data: {
                context: {},
                data: {
                   filter: filters
                }
             }
        } );

        // pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

            this.setState( {
                isBarChartFetching: false,
                barGraphNetworkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
                barGraphData: {},
            } );
        } else {
            this.setState( {
                isBarChartFetching: false,
                barGraphNetworkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                barGraphData: data,
            } );
        }
    }

    // fetch Data for the pie chart
    async fetchDataForChart( categoryId ) {
        log.debug( 'CallStatisticsReports.fetchDataForChart()' );

        pageLevelPreloader.show();

        // cancel ongoing request
        this.callStatisticsReportsService.cancel();

        // get status and service data
        const { status, data } = await this.callStatisticsReportsService.fetchChartData( {
            url: this.state.barGraphData.config.reportConfig.url,
            data: {
                context: {},
                data: {
                   filter: this.state.componentData.filterData.filter,
                   [ this.state.barGraphData.config.reportConfig.payloadKey ]: categoryId
                }
             }
        } );

        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

            errorOverlay.open();
            
            this.setState( {
                isPieChartFetching: false,
                pieChartData: {},
                isPieChartVisible: false
            } );
        } else {
            this.setState( {
                isPieChartFetching: false,
                pieChartData: data,
            } );
        }
    }

    // handle click of option selection
    handleOptionSelect( { option, options, selected, optionType, isSelectAllVisible } ) {
		log.debug( 'CallStatisticsReports.handleOptionSelect()' );

        const selectedOptions =  'callRange' !== optionType && 'cag' !== optionType ? this.getSelection( option, options, selected, isSelectAllVisible ) : null;
        const data = { ...this.state.componentData };

        data.filterData.filter[ optionType ] = 'callRange' !== optionType && 'cag' !== optionType ? selectedOptions : options;
        
        if( 'callType' === optionType || 'callerType' === optionType ) {
            const _filterData = reportCallCallerTypeselectionResolver( { filterData: data.filterData, optionType, optionValue: get( option, 'value' ) } );
            set( data, 'filterData', _filterData );
        }

        this.setState( {
            componentData: data,
            isBarChartFetching: true,
            networkStatus: undefined,
            isPieChartVisible: false,
        }, () => {

            // pageLevelPreloader.show();
            this.fetchDataForGraph();
        } );
    }

    // handle click on the bar chart
    handleBarChartClick( { payload } ) {
        log.debug( 'CallStatisticsReports.handleBarChartClick()' );
        this.setState( {
            isPieChartFetching: true,
            networkStatus: undefined,
            isPieChartVisible: true,
        }, () => this.fetchDataForChart( payload.id ) );
    }

    // get selected options
    getSelection( option, options, selected, isSelectAllVisible ) {
        log.debug( 'CallStatisticsReports.getSelection()' );

        // new selected value
        let selection = [];
        const selectableOptions = filter( options, _option => ! isEmpty( _option[ 'value' ] ) ); // `labels` excluded
        const selectableOptionsExceptSelectAll = isSelectAllVisible ? filter( selectableOptions, _option => _option[ 'value' ] !== SELECT_ALL_OPTION_VALUE ) : selectableOptions; // remove `selectAll` option

        if( includes( selected, option[ 'value' ] ) ) {
            if( isSelectAllVisible && option[ 'value' ] === SELECT_ALL_OPTION_VALUE ) {
                selection = []; // wipe all selection when `selectAll` option is unselected
            } else {
                selection = filter( selected, _value => {
                    return _value !== option[ 'value' ] && _value !== SELECT_ALL_OPTION_VALUE; // remove `selectAll` option as well
                } );
            }
        } else {
            if( isSelectAllVisible && option[ 'value' ] === SELECT_ALL_OPTION_VALUE ) {
                selection = map( selectableOptions, 'value' ); // select all options when `selectAll` option is selected
            } else {
                selection = selected.concat( option[ 'value' ] );
            }
        }

        // if all options are selected, also select `selectAll` option
        if( isSelectAllVisible && ! includes( selection, SELECT_ALL_OPTION_VALUE ) ) {
            const areAllOptionsSelected = every( selectableOptionsExceptSelectAll, ( _option ) => includes( selection, _option[ 'value' ] ) );

            if( areAllOptionsSelected ) {
                selection = map( selectableOptions, 'value' );
            }
        }
        return selection;
    }

    // handle cag changes when apply button is clicked
    handleCagChange( values = {} ) {
        log.debug( 'CagFilterDemo.handleCagChange()' );

        this.handleOptionSelect( { options: values, optionType: 'cag' } );
    }

    /*********************************************/
    
    /**
     * @desc When component unMounts, cleanup resources.
     */
    componentWillUnmount() {
        this.callStatisticsReportsService.cancel(); // cancel ongoing requests
    }
    
    /*********************************************/

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <CallStatisticsReportsView
                isFetching={ this.state.isFetching }
                isBarChartFetching={ this.state.isBarChartFetching }
                isPieChartFetching={ this.state.isPieChartFetching }
                networkStatus={ this.state.networkStatus }
                barGraphNetworkStatus={ this.state.barGraphNetworkStatus }
                onOptionSelect={ this.handleOptionSelect }
                barGraphData={ this.state.barGraphData }
                pieChartData={ this.state.pieChartData }
                onBarChartClickHandler={ this.handleBarChartClick }
                onCagChange={ this.handleCagChange }
                isPieChartVisible={ this.state.isPieChartVisible }
                { ...this.state.componentData }
            />
        );
    }
}

// set display name
CallStatisticsReports.displayName = 'CallStatisticsReports';

// set default props
CallStatisticsReports.defaultProps = {};

// prop types
CallStatisticsReports.propTypes = {};
